import * as Sentry from "@sentry/react"

import { type HandledError } from "../errors/handled-error"
import { normalizeTransportEndpoint } from "./transport-failures"

interface HandledErrorReporterOptions {
  capture?: (error: HandledError) => void
  maxEventsPerSession?: number
}

export interface UnexpectedFailureContext {
  flow: string
  endpoint?: string | null
}

const DEFAULT_MAX_EVENTS_PER_SESSION = 20

const captureToSentry = (error: HandledError): void => {
  Sentry.captureMessage("Handled API error", {
    level: "warning",
    tags: {
      flow: error.flow,
      endpoint: error.endpoint,
      method: error.method,
      status_class: error.statusClass,
      error_code: error.code
    },
    extra: {
      status: error.status,
      field_count: error.fieldErrors?.length ?? 0
    },
    fingerprint: ["handled-api-error", error.flow, error.statusClass, error.code]
  })
}

// 4xx answers are the product talking to the user, transport failures already
// reach Sentry through the interceptor, and a cancel is the user's own choice.
export const shouldReportHandledError = (error: HandledError): boolean => {
  if (error.code === "cancelled") return false
  if (error.statusClass === "5xx") return true
  if (error.code === "malformed") return true
  return error.statusClass === "unknown" && error.code === "unknown"
}

export const createHandledErrorReporter = ({
  capture = captureToSentry,
  maxEventsPerSession = DEFAULT_MAX_EVENTS_PER_SESSION
}: HandledErrorReporterOptions = {}) => {
  const reported = new Set<string>()

  return (error: HandledError): boolean => {
    if (!shouldReportHandledError(error)) return false

    const key = `${error.flow}|${error.endpoint}|${error.statusClass}|${error.code}`
    if (reported.has(key) || reported.size >= maxEventsPerSession) return false

    try {
      capture(error)
    } catch {
      return false
    }

    reported.add(key)
    return true
  }
}

export const reportHandledError = createHandledErrorReporter()

export const reportUnexpectedFailure = (
  failure: unknown,
  { flow, endpoint }: UnexpectedFailureContext
): void => {
  try {
    Sentry.captureException(failure, {
      tags: { flow, endpoint: normalizeTransportEndpoint(endpoint) },
      fingerprint: ["unexpected-failure", flow]
    })
  } catch {
    // Reporting must never mask the failure it describes.
  }
}
